
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage, ChatGroup } from '@/types/chat';
import { toast } from 'sonner';

interface User {
  id: string;
  username: string;
  userType: 'supabase' | 'internal';
}

interface UseEnhancedChatRealtimeProps {
  selectedGroup: ChatGroup | null;
  user: User | null;
  onMessageReceived: (message: ChatMessage) => void;
}

export const useEnhancedChatRealtime = ({ selectedGroup, user, onMessageReceived }: UseEnhancedChatRealtimeProps) => {
  useEffect(() => {
    if (!selectedGroup || !user) {
      console.log('=== REAL-TIME SETUP SKIPPED ===');
      return;
    }

    console.log('=== SETTING UP ENHANCED REAL-TIME SYSTEM ===');
    console.log('Group:', selectedGroup.chat_id, 'User:', user.username, 'Type:', user.userType);

    let supabaseChannel: any = null;
    let storageListener: ((e: StorageEvent) => void) | null = null;
    let messageListener: ((e: CustomEvent) => void) | null = null;

    // FOR SUPABASE USERS: Enhanced Supabase real-time subscription
    if (user.userType === 'supabase') {
      console.log('🔄 Setting up Supabase real-time for user:', user.username);
      
      supabaseChannel = supabase
        .channel(`chat-${selectedGroup.chat_id}-${user.id}-enhanced`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',  
            schema: 'public',
            table: 'chat_messages',
            filter: `chat_group_id=eq.${selectedGroup.chat_id}`
          },
          async (payload) => {
            console.log('=== SUPABASE REAL-TIME MESSAGE ===');
            const newMessage = payload.new as ChatMessage;
            
            // Skip messages from current user to prevent duplicates
            if (String(newMessage.user_id) === String(user.id)) {
              console.log('⏭️ Skipping own message from Supabase real-time');
              return;
            }
            
            try {
              // Get complete message without foreign key join
              const { data: messageData, error } = await supabase
                .from('chat_messages')
                .select('*')
                .eq('message_id', newMessage.message_id)
                .maybeSingle();

              if (error || !messageData) {
                console.error('Error fetching message details:', error);
                return;
              }

              // Get user profile separately to handle both Supabase and internal users
              const [supabaseProfile, internalUser] = await Promise.all([
                supabase
                  .from('profiles')
                  .select('username, first_name, last_name, role')
                  .eq('id', messageData.user_id)
                  .maybeSingle(),
                supabase
                  .from('internal_users')
                  .select('username, role')
                  .eq('id', messageData.user_id)
                  .maybeSingle()
              ]);

              // Create user profile object
              let userProfile;
              if (supabaseProfile.data) {
                userProfile = {
                  username: supabaseProfile.data.username || supabaseProfile.data.first_name || 'User',
                  first_name: supabaseProfile.data.first_name || '',
                  last_name: supabaseProfile.data.last_name || '',
                  role: supabaseProfile.data.role || 'player'
                };
              } else if (internalUser.data) {
                userProfile = {
                  username: internalUser.data.username || 'New School User',
                  first_name: internalUser.data.username || '',
                  last_name: '',
                  role: internalUser.data.role || 'player'
                };
              } else {
                userProfile = {
                  username: `User-${messageData.user_id?.slice(0, 8) || 'Unknown'}`,
                  first_name: '',
                  last_name: '',
                  role: 'player'
                };
              }

              const messageWithProfile = {
                ...messageData,
                profiles: userProfile
              };

              const enhancedMessage = {
                ...messageWithProfile,
                user_profile: {
                  username: userProfile.username,
                  first_name: userProfile.first_name || userProfile.username,
                  last_name: userProfile.last_name || '',
                  role: userProfile.role || 'player'
                }
              } as ChatMessage;

              console.log('✅ Processing Supabase real-time message:', enhancedMessage.message);
              onMessageReceived(enhancedMessage);

              // Dispatch custom event if it's a reply
              if (enhancedMessage.parent_message_id) {
                window.dispatchEvent(new CustomEvent('chat-reply-received', {
                  detail: enhancedMessage
                }));
              }

              // Show toast notification
              if (enhancedMessage.user_profile?.username) {
                const messagePreview = enhancedMessage.message.length > 50 
                  ? enhancedMessage.message.substring(0, 50) + '...' 
                  : enhancedMessage.message;
                  
                toast(`💬 ${enhancedMessage.user_profile.username}`, {
                  description: messagePreview,
                  duration: 4000,
                });
              }
            } catch (error) {
              console.error('❌ Error processing Supabase real-time message:', error);
            }
          }
        )
        .subscribe((status) => {
          console.log('📡 Supabase real-time status:', status);
        });
    }

    // FOR ALL USERS: Enhanced localStorage-based real-time for cross-tab sync
    console.log('🔄 Setting up localStorage real-time system...');
    
    storageListener = (event: StorageEvent) => {
      if (event.key === 'internal_chat_messages' && event.newValue && selectedGroup) {
        try {
          console.log('=== STORAGE CHANGE DETECTED ===');
          const newMessages = JSON.parse(event.newValue);
          const oldMessages = event.oldValue ? JSON.parse(event.oldValue) : [];
          
          const newGroupMessages = newMessages.filter((msg: ChatMessage) => 
            msg.chat_group_id === selectedGroup.chat_id &&
            !oldMessages.some((oldMsg: ChatMessage) => oldMsg.message_id === msg.message_id)
          );
          
          newGroupMessages.forEach((message: ChatMessage) => {
            if (String(message.user_id) !== String(user.id)) {
              console.log('✅ Processing localStorage real-time message:', message.message);
              onMessageReceived(message);
              
              if (message.user_profile?.username) {
                const messagePreview = message.message.length > 50 
                  ? message.message.substring(0, 50) + '...' 
                  : message.message;
                  
                toast(`💬 ${message.user_profile.username}`, {
                  description: messagePreview,
                  duration: 4000,
                });
              }
            }
          });
        } catch (error) {
          console.error('❌ Error parsing localStorage event:', error);
        }
      }
    };

    // Enhanced message listener for immediate same-tab updates
    messageListener = (event: CustomEvent<{ message: ChatMessage; groupId: string; sender: User }>) => {
      const { message, groupId, sender } = event.detail;
      
      if (groupId === selectedGroup.chat_id && sender.id !== user.id) {
        console.log('✅ Processing immediate message event:', message.message);
        onMessageReceived(message);
        
        if (message.user_profile?.username) {
          const messagePreview = message.message.length > 50 
            ? message.message.substring(0, 50) + '...' 
            : message.message;
            
          toast(`💬 ${message.user_profile.username}`, {
            description: messagePreview,
            duration: 4000,
          });
        }
      }
    };

    // Add all event listeners
    window.addEventListener('storage', storageListener);
    window.addEventListener('chat-message-received', messageListener as EventListener);

    console.log('🎯 Enhanced real-time system fully initialized for:', user.username);

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up enhanced real-time subscriptions...');
      
      if (supabaseChannel) {
        supabase.removeChannel(supabaseChannel);
        console.log('✅ Supabase channel removed');
      }
      
      if (storageListener) {
        window.removeEventListener('storage', storageListener);
        console.log('✅ Storage listener removed');
      }
      
      if (messageListener) {
        window.removeEventListener('chat-message-received', messageListener as EventListener);
        console.log('✅ Message listener removed');
      }
    };
  }, [selectedGroup, user, onMessageReceived]);
};
